import { Component, ViewChild, inject } from '@angular/core';
import { AppLogo } from '@app/components/app-logo';
import { AppMenuComponent } from '@layouts/components/sidenav/components/app-menu/app-menu';
import { SimplebarAngularModule } from 'simplebar-angular';
import { menuItems } from '@layouts/components/data';
import { MenuItemType } from '@/app/types/layout';
import { UiLocaleService } from '@core/services/ui-locale.service';
import { UiTranslationService } from '@core/services/ui-translation.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { PaymentPlanUpgradeModal } from '@/app/views/modals/payment-plan-upgrade-modal/payment-plan-upgrade-modal';
import { AuthService, PermissionFlag } from '@core/services/auth.service';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-sidenav',
  imports: [AppLogo, AppMenuComponent, SimplebarAngularModule],
  templateUrl: './sidenav.html',
  styles: ``,
})
export class Sidenav {
  private readonly uiLocaleService = inject(UiLocaleService);
  private readonly modalService = inject(NgbModal);
  private readonly authService = inject(AuthService);
  private readonly toastr = inject(ToastrService);
  private readonly navigationTranslations =
    inject(UiTranslationService).namespace('navigation');
  @ViewChild(AppMenuComponent) menuComp!: AppMenuComponent;
  protected filterText = '';
  protected showNoResults = false;

  protected readonly menuItems = menuItems;

  get filteredMenuItems() {
    this.uiLocaleService.locale();

    if (!this.filterText.trim()) return this.menuItems;

    const search = this.filterText.trim().toLowerCase();

    const deepFilter = (
      items: MenuItemType[],
      includeAll = false,
    ): MenuItemType[] => {
      return items
        .map((item) => {
          const selfMatch = this.resolveLabel(item)
            .toLowerCase()
            .includes(search);

          // If parent matched OR an ancestor already matched, keep full subtree untouched
          if (selfMatch || includeAll) {
            return {
              ...item,
              // keep original children (not filtered) when locked open
              children: item.children ? [...item.children] : undefined,
            };
          }

          // Otherwise, filter children recursively
          if (item.children && item.children.length) {
            const filteredChildren = deepFilter(item.children, false);
            if (filteredChildren.length) {
              return { ...item, children: filteredChildren };
            }
          }

          // Titles are kept only if they match or have matching descendants
          if (item.isTitle) return null;

          // Leaf: keep only if it matches
          return selfMatch ? item : null;
        })
        .filter((x): x is MenuItemType => x !== null);
    };

    return deepFilter(this.menuItems);
  }

  updateFilterText(e: Event) {
    const target = e.target as HTMLInputElement;
    this.filterText = target.value;
    this.showNoResults = !this.filteredMenuItems.length;
    this.menuComp.expandFilteredPaths(this.filteredMenuItems);
  }

  protected onMenuAction(action: MenuItemType['action']): void {
    if (action !== 'upgrade') {
      return;
    }

    if (!this.authService.hasPermission('PaymentPlan', PermissionFlag.Update)) {
      this.toastr.error(
        this.navigationTranslations.tr('paymentPlanPermissionDenied'),
      );
      return;
    }

    this.modalService.open(PaymentPlanUpgradeModal, {
      size: 'xl',
      scrollable: true,
      centered: true,
      windowClass: 'payment-plan-upgrade-window',
    });
  }

  private resolveLabel(item: MenuItemType): string {
    if (!item.labelKey) {
      return item.label ?? '';
    }

    const translated = this.navigationTranslations.tr(item.labelKey);
    return translated === item.labelKey ? (item.label ?? '') : translated;
  }
}
